import { widgetDefinesSignalIdentity } from "@/features/signals/signalConfigFromWidget";
import type { SignalConfig } from "@/features/signals/types";
import type { DashboardWidget } from "./types";

/**
 * Aplica o `SignalConfig` compartilhado do sinal físico sobre um widget, para
 * exibição. É a fonte ÚNICA de "quem herda o quê".
 *
 * INVARIANTE: um widget herda a identidade do sinal (rótulo, unidade, escala,
 * decimais, activeWhen, stateLabels) se, e SOMENTE SE, ele também a DEFINE
 * (`widgetDefinesSignalIdentity`). Ou seja, "lê da config sse escreve na config".
 *
 *  - Widgets de IDENTIDADE (gauge/número/LED/produção/valor): escrevem E
 *    herdam. Editar qualquer campo volta pela config e aparece no painel.
 *  - GRÁFICOS (linha/barra) e VISÕES DERIVADAS (disponibilidade/utilização):
 *    identidade própria — não escrevem nem herdam, então TODO campo editado
 *    vale na hora. Antes, o card de disponibilidade mostrava o `signalConfig.label`
 *    antigo em vez do nome amigável editado, e um gráfico podia herdar uma
 *    unidade que não era a sua (edição de unidade/decimais ignorada).
 */
export function resolveWidgetForSignalConfig(
  widget: DashboardWidget,
  signalConfig: SignalConfig | undefined,
): DashboardWidget {
  if (!signalConfig || !widgetDefinesSignalIdentity(widget)) return widget;

  return {
    ...widget,
    // O nome compartilhado do sinal continua valendo entre os cards da mesma
    // fonte — e o que faz renomear um sinal alcancar os outros. Quem arbitra
    // contra o `/meta` e o `SignalCard`, que e onde o nome declarado chega.
    title: signalConfig.label,
    settings: {
      ...widget.settings,
      signalKind: signalConfig.kind ?? widget.settings.signalKind,
      unit: signalConfig.unit ?? widget.settings.unit,
      min: signalConfig.scale?.min ?? widget.settings.min,
      max: signalConfig.scale?.max ?? widget.settings.max,
      decimals: signalConfig.scale?.decimals ?? widget.settings.decimals,
      activeWhen: signalConfig.activeWhen ?? widget.settings.activeWhen,
      stateLabels: signalConfig.stateLabels ?? widget.settings.stateLabels,
    },
  };
}
